import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings, getApiKeyStatus, saveApiKey, deleteApiKey,
  getBraveKeyStatus, saveBraveKey, deleteBraveKey,
  getMatchSettings, updateMatchSettings, resetMatchSettings, MatchSettings } from '../api/settings';
import { PositionMatrixManager } from '../components/settings/PositionMatrixManager';
import { TwoFactorPanel } from '../components/settings/TwoFactorPanel';
import { EmailAccountsManager } from '../components/settings/EmailAccountsManager';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', fontSize: '13px', background: 'var(--surface)', color: 'var(--text)',
  fontFamily: 'inherit',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', marginBottom: '5px',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '13px', fontWeight: 600, color: 'var(--text)',
  borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '16px',
};
const hintStyle: React.CSSProperties = {
  fontSize: '11px', color: 'var(--text3)', marginTop: '4px',
};

export function SettingsPage() {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKeyMasked, setApiKeyMasked] = useState<string | null>(null);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [apiKeyMsg, setApiKeyMsg] = useState<string | null>(null);

  // Brave Search API Key (Modul A — Webseiten-Auto-Suche)
  const [braveInput, setBraveInput] = useState('');
  const [braveMasked, setBraveMasked] = useState<string | null>(null);
  const [hasBraveKey, setHasBraveKey] = useState(false);
  const [showBraveKey, setShowBraveKey] = useState(false);
  const [braveSaving, setBraveSaving] = useState(false);
  const [braveMsg, setBraveMsg] = useState<string | null>(null);

  // Match-Scoring-Settings
  const [matchCfg, setMatchCfg] = useState<MatchSettings | null>(null);
  const [matchSaving, setMatchSaving] = useState(false);
  const [matchMsg, setMatchMsg] = useState<string | null>(null);

  useEffect(() => {
    getSettings()
      .then(s => setSettings(s))
      .catch(() => setError('Einstellungen konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
    getApiKeyStatus()
      .then(({ has_key, masked }) => { setHasApiKey(has_key); setApiKeyMasked(masked); })
      .catch(() => {});
    getBraveKeyStatus()
      .then(({ has_key, masked }) => { setHasBraveKey(has_key); setBraveMasked(masked); })
      .catch(() => {});
    getMatchSettings()
      .then(r => setMatchCfg(r.data))
      .catch(() => {});
  }, []);

  const setMatch = (k: keyof MatchSettings, v: MatchSettings[keyof MatchSettings]) => {
    setMatchCfg(prev => prev ? { ...prev, [k]: v } : prev);
    setMatchMsg(null);
  };
  const saveMatch = async () => {
    if (!matchCfg) return;
    setMatchSaving(true); setMatchMsg(null);
    try {
      const updated = await updateMatchSettings(matchCfg);
      setMatchCfg(updated);
      setMatchMsg('✓ Gespeichert');
      setTimeout(() => setMatchMsg(null), 3000);
    } catch { setMatchMsg('Fehler beim Speichern'); }
    finally { setMatchSaving(false); }
  };
  const resetMatch = async () => {
    if (!confirm('Match-Scoring auf Defaults zurücksetzen?')) return;
    setMatchSaving(true);
    try { setMatchCfg(await resetMatchSettings()); setMatchMsg('✓ Zurückgesetzt'); }
    catch { setMatchMsg('Fehler beim Zurücksetzen'); }
    finally { setMatchSaving(false); }
  };

  const set = (key: string, value: string) =>
    setSettings(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError(null);
    try {
      await updateSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-header-title">Einstellungen</div>
          <div className="page-header-sub">API-Zugang & KI-Automatisierung</div>
        </div>
      </div>
      <div className="content">
        <div style={{ maxWidth: '800px' }}>
          {error && (
            <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gap: '24px' }}>
            {/* 2FA / TOTP */}
            <div className="card">
              <div className="card-header"><span className="card-title">Zwei-Faktor-Authentifizierung</span></div>
              <div className="card-body">
                <TwoFactorPanel />
              </div>
            </div>

            {/* Mail-Konten (Multi-Account SMTP + IMAP) */}
            <div className="card">
              <div className="card-header"><span className="card-title">Mail-Konten (SMTP + IMAP)</span></div>
              <div className="card-body">
                <EmailAccountsManager />
              </div>
            </div>

            {/* Positionen-Matrix — Rangordnung der Berufsbezeichnungen */}
            <div className="card">
              <div className="card-header"><span className="card-title">Positionen-Matrix (Hauptansprechpartner / Postempfänger)</span></div>
              <div className="card-body">
                <PositionMatrixManager />
              </div>
            </div>

            {/* Hilfe / Dokumentation */}
            <div className="card">
              <div className="card-header"><span className="card-title">Hilfe &amp; Dokumentation</span></div>
              <div className="card-body">
                <div style={{ fontSize: '13px', color: 'var(--text3)', marginBottom: '12px', lineHeight: 1.6 }}>
                  Kompaktes Benutzerhandbuch mit den wichtigsten Bedienelementen.
                  Version v1.9.0.
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <a href="/docs/Benutzerhandbuch.html" target="_blank" rel="noopener noreferrer"
                    style={{
                      padding: '7px 14px', background: 'var(--accent)', color: '#fff',
                      textDecoration: 'none', borderRadius: 'var(--radius)',
                      fontSize: '13px', fontWeight: 500,
                    }}>
                    📖 Handbuch
                  </a>
                  <a href="https://github.com/MichaFrankfurt/investment-crm/blob/main/CHANGELOG.md"
                    target="_blank" rel="noopener noreferrer"
                    style={{
                      padding: '7px 14px', background: 'var(--surface)', color: 'var(--text2)',
                      textDecoration: 'none', borderRadius: 'var(--radius)',
                      fontSize: '13px', border: '1px solid var(--border2)',
                    }}>
                    📜 Changelog
                  </a>
                </div>
              </div>
            </div>

            {/* API Key */}
            <div className="card">
              <div className="card-header"><span className="card-title">AI-Zuordnung (Claude API)</span></div>
              <div className="card-body">
                <div style={{ fontSize: '13px', color: 'var(--text3)', marginBottom: '16px', lineHeight: 1.6 }}>
                  Fuer die intelligente AI-Zuordnung wird ein Anthropic API-Key benoetigt.
                  Der Key wird verschluesselt gespeichert. Erstelle einen Key unter{' '}
                  <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)', textDecoration: 'underline' }}>
                    console.anthropic.com
                  </a>.
                </div>
                <label style={labelStyle}>Anthropic API Key</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                  <input style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: '13px' }}
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKeyInput || (hasApiKey ? '••••••••••••••••••••' : '')}
                    onChange={e => { setApiKeyInput(e.target.value); setApiKeyMsg(null); }}
                    placeholder="sk-ant-api03-..." />
                  <button type="button" onClick={() => setShowApiKey(!showApiKey)}
                    style={{ padding: '8px 14px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}>
                    {showApiKey ? 'Verbergen' : 'Anzeigen'}
                  </button>
                </div>
                {apiKeyMsg && (
                  <div style={{ fontSize: '12px', marginBottom: '8px', color: apiKeyMsg.startsWith('✓') ? '#2e7d32' : 'var(--accent)' }}>{apiKeyMsg}</div>
                )}
                {hasApiKey && !apiKeyInput && (
                  <div style={{ fontSize: '12px', color: '#2e7d32', marginBottom: '8px' }}>
                    API Key gespeichert {apiKeyMasked ? `(${apiKeyMasked})` : ''}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                  <button type="button" disabled={apiKeySaving || !apiKeyInput}
                    onClick={async () => {
                      setApiKeySaving(true); setApiKeyMsg(null);
                      try {
                        const res = await saveApiKey(apiKeyInput);
                        setHasApiKey(true); setApiKeyMasked(res.masked); setApiKeyInput('');
                        setApiKeyMsg('✓ API Key gespeichert');
                      } catch { setApiKeyMsg('Fehler beim Speichern.'); }
                      finally { setApiKeySaving(false); }
                    }}
                    style={{ padding: '9px 22px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: (apiKeySaving || !apiKeyInput) ? 'not-allowed' : 'pointer', opacity: (apiKeySaving || !apiKeyInput) ? 0.6 : 1 }}>
                    {apiKeySaving ? 'Wird gespeichert...' : 'API Key speichern'}
                  </button>
                  {hasApiKey && (
                    <button type="button"
                      onClick={async () => {
                        setApiKeySaving(true);
                        try { await deleteApiKey(); setHasApiKey(false); setApiKeyMasked(null); setApiKeyInput(''); setApiKeyMsg('API Key entfernt.'); }
                        catch { setApiKeyMsg('Fehler beim Entfernen.'); }
                        finally { setApiKeySaving(false); }
                      }}
                      style={{ padding: '9px 22px', background: 'transparent', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
                      Key entfernen
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Brave Search API Key — für Modul A (Webseiten-Auto-Suche) */}
            <div className="card">
              <div className="card-header"><span className="card-title">Webseiten-Recherche (Brave Search API)</span></div>
              <div className="card-body">
                <div style={{ fontSize: '13px', color: 'var(--text3)', marginBottom: '16px', lineHeight: 1.6 }}>
                  Für die automatische Suche von Firmen-Webseiten anhand des Investorennamens.
                  Free Tier: 2.000 Anfragen/Monat. Key registrieren unter{' '}
                  <a href="https://api-dashboard.search.brave.com/register" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)', textDecoration: 'underline' }}>
                    api-dashboard.search.brave.com/register
                  </a>.
                </div>
                <label style={labelStyle}>Brave Search API Key</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                  <input style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: '13px' }}
                    type={showBraveKey ? 'text' : 'password'}
                    value={braveInput || (hasBraveKey ? '••••••••••••••••••••' : '')}
                    onChange={e => { setBraveInput(e.target.value); setBraveMsg(null); }}
                    placeholder="BSA..." />
                  <button type="button" onClick={() => setShowBraveKey(!showBraveKey)}
                    style={{ padding: '8px 14px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}>
                    {showBraveKey ? 'Verbergen' : 'Anzeigen'}
                  </button>
                </div>
                {braveMsg && (
                  <div style={{ fontSize: '12px', marginBottom: '8px', color: braveMsg.startsWith('✓') ? '#2e7d32' : 'var(--accent)' }}>{braveMsg}</div>
                )}
                {hasBraveKey && !braveInput && (
                  <div style={{ fontSize: '12px', color: '#2e7d32', marginBottom: '8px' }}>
                    API Key gespeichert {braveMasked ? `(${braveMasked})` : ''}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                  <button type="button" disabled={braveSaving || !braveInput}
                    onClick={async () => {
                      setBraveSaving(true); setBraveMsg(null);
                      try {
                        const res = await saveBraveKey(braveInput);
                        setHasBraveKey(true); setBraveMasked(res.masked); setBraveInput('');
                        setBraveMsg('✓ Brave API Key gespeichert');
                      } catch { setBraveMsg('Fehler beim Speichern.'); }
                      finally { setBraveSaving(false); }
                    }}
                    style={{ padding: '9px 22px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: (braveSaving || !braveInput) ? 'not-allowed' : 'pointer', opacity: (braveSaving || !braveInput) ? 0.6 : 1 }}>
                    {braveSaving ? 'Wird gespeichert...' : 'API Key speichern'}
                  </button>
                  {hasBraveKey && (
                    <button type="button"
                      onClick={async () => {
                        setBraveSaving(true);
                        try { await deleteBraveKey(); setHasBraveKey(false); setBraveMasked(null); setBraveInput(''); setBraveMsg('Brave API Key entfernt.'); }
                        catch { setBraveMsg('Fehler beim Entfernen.'); }
                        finally { setBraveSaving(false); }
                      }}
                      style={{ padding: '9px 22px', background: 'transparent', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
                      Key entfernen
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Match-Scoring */}
            {matchCfg && (
              <div className="card">
                <div className="card-header">
                  <span className="card-title">Match-Scoring (Mandate ↔ Investorenprofile)</span>
                </div>
                <div className="card-body">
                  <div style={{ fontSize: '13px', color: 'var(--text3)', marginBottom: 16, lineHeight: 1.6 }}>
                    Standard-Einstellungen für die Vorschläge "Passende Investoren". Pro Mandat können diese
                    Werte überschrieben werden.
                  </div>

                  {/* Filter-Modi */}
                  <div style={sectionTitle}>Filter-Modi</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    <div>
                      <label style={labelStyle}>Land-Filter</label>
                      <select style={inputStyle} value={matchCfg.country_mode}
                        onChange={e => setMatch('country_mode', e.target.value as MatchSettings['country_mode'])}>
                        <option value="hard">Hart (Land-Mismatch ausschließen)</option>
                        <option value="soft">Weich (Score-Abzug)</option>
                        <option value="off">Aus (ignorieren)</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Stadt-Match</label>
                      <select style={inputStyle} value={matchCfg.city_mode}
                        onChange={e => setMatch('city_mode', e.target.value as MatchSettings['city_mode'])}>
                        <option value="fuzzy">Fuzzy (z.B. "Frankfurt" = "Frankfurt am Main")</option>
                        <option value="strict">Strict (exakt)</option>
                        <option value="off">Aus (ignorieren)</option>
                      </select>
                    </div>
                  </div>

                  {/* Toleranzen */}
                  <div style={sectionTitle}>Toleranzen (% außerhalb Range tolerierbar)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                    {([['volume_tolerance_pct','Volumen %'], ['size_tolerance_pct','Fläche %'], ['units_tolerance_pct','Einheiten %'], ['walt_tolerance_pct','WALT %'], ['year_tolerance_years','Baujahr ±Jahre']] as Array<[keyof MatchSettings, string]>).map(([k, lbl]) => (
                      <div key={k}>
                        <label style={labelStyle}>{lbl}</label>
                        <input type="number" min={0} max={500} style={inputStyle}
                          value={matchCfg[k] as number}
                          onChange={e => setMatch(k, parseInt(e.target.value || '0'))}/>
                      </div>
                    ))}
                  </div>

                  {/* Boni */}
                  <div style={sectionTitle}>Boni (Punktwerte 0–100)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                    {([
                      ['bonus_location_quality','Lage-Qualität'],
                      ['bonus_country','Land-Match'],
                      ['bonus_city_fuzzy','Stadt-Match (fuzzy)'],
                      ['bonus_city_strict_extra','Stadt exakt (Extra)'],
                      ['bonus_risk','Risk-Profil'],
                      ['bonus_tenants','Mieter/Betreiber'],
                      ['bonus_esg','ESG'],
                      ['bonus_deal_structure','Deal-Struktur'],
                      ['bonus_volume_centered','Volumen mittig'],
                    ] as Array<[keyof MatchSettings, string]>).map(([k, lbl]) => (
                      <div key={k}>
                        <label style={labelStyle}>{lbl}</label>
                        <input type="number" min={0} max={100} style={inputStyle}
                          value={matchCfg[k] as number}
                          onChange={e => setMatch(k, parseInt(e.target.value || '0'))}/>
                      </div>
                    ))}
                  </div>

                  {/* Schwellen + Gewichte */}
                  <div style={sectionTitle}>Score-Schwellen & Default-Gewichte</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                    {([
                      ['min_score','Cutoff (unter Score → ausblenden)'],
                      ['medium_score','Gelber Score ab'],
                      ['high_score','Grüner Score ab'],
                      ['weight_asset_default','Gewicht Asset'],
                      ['weight_location_default','Gewicht Lage'],
                      ['weight_financial_default','Gewicht Finanzen'],
                    ] as Array<[keyof MatchSettings, string]>).map(([k, lbl]) => (
                      <div key={k}>
                        <label style={labelStyle}>{lbl}</label>
                        <input type="number" min={0} max={500} style={inputStyle}
                          value={matchCfg[k] as number}
                          onChange={e => setMatch(k, parseInt(e.target.value || '0'))}/>
                      </div>
                    ))}
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
                    <input type="checkbox" checked={matchCfg.respect_profile_weights}
                      onChange={e => setMatch('respect_profile_weights', e.target.checked)}/>
                    Profile-eigene Gewichte respektieren (statt Defaults oben)
                  </label>

                  {matchMsg && (
                    <div style={{ fontSize: 12, marginBottom: 8, color: matchMsg.startsWith('✓') ? '#2e7d32' : 'var(--accent)' }}>{matchMsg}</div>
                  )}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" disabled={matchSaving} onClick={saveMatch}
                      style={{ padding: '9px 22px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500, cursor: matchSaving ? 'wait' : 'pointer' }}>
                      {matchSaving ? 'Wird gespeichert...' : 'Match-Settings speichern'}
                    </button>
                    <button type="button" disabled={matchSaving} onClick={resetMatch}
                      style={{ padding: '9px 22px', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: 13, cursor: 'pointer' }}>
                      Auf Defaults zurücksetzen
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* KI & Automatisierung */}
            <div className="card">
              <div className="card-header"><span className="card-title">KI & Automatisierung</span></div>
              <div className="card-body">
                <div style={sectionTitle}>Kontakt-Recherche</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '16px' }}>
                  <input type="checkbox" checked={settings.contact_auto_refresh !== 'false'}
                    onChange={e => set('contact_auto_refresh', e.target.checked ? 'true' : 'false')} />
                  <span style={{ fontSize: '13px' }}>Kontakte automatisch aus dem Internet aktualisieren</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', opacity: settings.contact_auto_refresh === 'false' ? 0.4 : 1, pointerEvents: settings.contact_auto_refresh === 'false' ? 'none' : 'auto' }}>
                  <div>
                    <label style={labelStyle}>Kontakte prüfen alle (Tage)</label>
                    <input style={inputStyle} type="number" min="1" max="365"
                      value={settings.contact_refresh_days ?? '90'} onChange={e => set('contact_refresh_days', e.target.value)} placeholder="90" />
                    <div style={hintStyle}>Wie oft soll die KI die Kontakte aktualisieren?</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Prüfer-Name</label>
                    <input style={inputStyle} value={settings.reviewer_name ?? ''} onChange={e => set('reviewer_name', e.target.value)} placeholder="Jana Rubin" />
                    <div style={hintStyle}>Wird bei „Geprüft" und „Freigegeben" eingetragen.</div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button onClick={handleSave} disabled={saving}
                    style={{ padding: '9px 22px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                    {saving ? 'Wird gespeichert...' : saved ? 'Gespeichert' : 'Speichern'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
